import React, { lazy, Suspense } from 'react';
import { Route, Outlet, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import ProtectedRoute from '../ProtectedRoute';
import HomeRedirect from '../HomeRedirect';
import HomeGate from '../HomeGate';
import Intro from '../../pages/Intro/Intro';

/*
 * 공개 페이지(Intro)는 초기 로딩 속도가 중요해 정적으로 import하고,
 * 로그인 이후에만 쓰이는 대시보드/도구 페이지들은 lazy 로딩으로 분리해
 * 첫 진입 시 불필요한 JS(antd, handsontable, xlsx, 차트 라이브러리 등)를 받지 않도록 함.
 */
const MainLayout = lazy(() => import('./MainLayout'));
const Login = lazy(() => import('../../pages/Login/Login').then(m => ({ default: m.Login })));
const UpdatePost = lazy(() => import('../../pages/Intro/UpdatePost'));
const UpdateList = lazy(() => import('../../pages/Intro/UpdateList'));
const KakaoCallback = lazy(() => import('../../pages/Login/KakaoCallback'));
const KeyWordVer1 = lazy(() =>
    import('../../pages/KeyWordVer1/KeyWordVer1').then(m => ({ default: m.KeyWordVer1 }))
);
const KeyWordVer2 = lazy(() =>
    import('../../pages/KeyWordVer2/KeyWordVer2').then(m => ({ default: m.KeyWordVer2 }))
);
const FindMid = lazy(() => import('../../pages/FindMid/FindMid').then(m => ({ default: m.FindMid })));
const FindNaverRank = lazy(() =>
    import('../../pages/FindNaverRank/FindNaverRank').then(m => ({ default: m.FindNaverRank }))
);
const FindNaverRankTracking = lazy(() =>
    import('../../pages/FindNaverRankTracking/FindNaverRankTracking').then(m => ({
        default: m.FindNaverRankTracking,
    }))
);
const NaverIntegratedRank = lazy(() =>
    import('../../pages/NaverIntegratedRank').then(m => ({ default: m.NaverIntegratedRank }))
);
const FindProductByMid = lazy(() =>
    import('../../pages/FindProductByMid').then(m => ({ default: m.FindProductByMid }))
);
const ChangeUrl = lazy(() => import('../../pages/ChangeUrl/ChangeUrl').then(m => ({ default: m.ChangeUrl })));
const CountText = lazy(() => import('../../pages/CountText').then(m => ({ default: m.CountText })));
const KeywordRankVolumeCheck = lazy(() =>
    import('../../pages/KeywordRankVolumeCheck').then(m => ({ default: m.KeywordRankVolumeCheck }))
);
const IntegratedSearch = lazy(() => import('../../pages/IntegratedSearch'));
const ProductAnalysis = lazy(() => import('../../pages/ProductAnalysis/ProductAnalysis'));
const RankHistoryWatch = lazy(() => import('../../pages/RankHistoryWatch/RankHistoryWatch'));
const AutoComplete = lazy(() => import('../../pages/AutoComplete/AutoComplete'));
const ProductGenerate = lazy(() => import('../../pages/ProductGenerate/ProductGenerate'));
const DashBoard = lazy(() => import('../../pages/DashBoard/DashBoard').then(m => ({ default: m.DashBoard })));
const GradeGuide = lazy(() => import('../../pages/GradeGuide/GradeGuide').then(m => ({ default: m.GradeGuide })));
const PaymentSuccess = lazy(() =>
    import('../../pages/Payment/PaymentSuccess').then(m => ({ default: m.PaymentSuccess }))
);
const PaymentFail = lazy(() => import('../../pages/Payment/PaymentFail').then(m => ({ default: m.PaymentFail })));
const PaymentHistory = lazy(() => import('../../pages/Payment/PaymentHistory'));
const SlotGuide = lazy(() => import('../../pages/SlotGuide/SlotGuide').then(m => ({ default: m.SlotGuide })));
const SlotPurchase = lazy(() => import('../../pages/SlotPurchase/SlotPurchase'));

const RouteFallback = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }} />
);

// 조회 중 페이지 이탈 시 확인창(useBlocker)을 쓰려면 데이터 라우터(createBrowserRouter)가
// 필요하다 - 기존 <BrowserRouter>+<Routes>는 브라우저 자체 이탈(새로고침/탭 닫기)만
// beforeunload로 막을 수 있고 사이드바 메뉴 클릭 같은 앱 내부 이동은 못 막는다
// (2026-08-17). 트리 구조 자체는 그대로 두고 createRoutesFromElements로만 감싸서
// 라우트 30여 개를 손으로 옮겨적다 실수할 위험을 없앤다.
const SuspenseLayout = () => (
    <Suspense fallback={<RouteFallback />}>
        <Outlet />
    </Suspense>
);

const routes = createRoutesFromElements(
    <Route element={<SuspenseLayout />}>
        {/* 공개 페이지 (인증 불필요) */}
        <Route path="/" element={<HomeGate />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/kakao/callback" element={<KakaoCallback />} />
        <Route path="/intro" element={<Intro />} />
        <Route path="/update/:idSlug" element={<UpdatePost />} />
        <Route path="/updates" element={<UpdateList />} />

        {/* 보호된 라우트들은 MainLayout 내부에서 렌더링 */}
        <Route
            element={
                <ProtectedRoute>
                    <MainLayout />
                </ProtectedRoute>
            }
        >
            <Route path="/keywordVer1" element={<KeyWordVer1 />} />
            <Route path="/keywordVer2" element={<KeyWordVer2 />} />
            <Route path="/NaverIntegratedRank" element={<NaverIntegratedRank />} />
            <Route path="/findMid" element={<FindMid />} />
            <Route path="/findProductByMid" element={<FindProductByMid />} />
            <Route path="/findNaverRank" element={<FindNaverRank />} />
            <Route path="/findNaverRankTracking" element={<FindNaverRankTracking />} />
            <Route path="/changeUrl" element={<ChangeUrl />} />
            <Route path="/countText" element={<CountText />} />
            <Route path="/dashboard" element={<DashBoard />} />
            <Route path="/main" element={<HomeRedirect />} />
            <Route path="/NaverSaleAnalysis" element={<HomeRedirect />} />
            <Route path="/NaverIndexAnalysis" element={<HomeRedirect />} />
            <Route path="/KeywordRankVolumeCheck" element={<KeywordRankVolumeCheck />} />
            <Route path="/integratedSearch" element={<IntegratedSearch />} />
            <Route path="/product-analysis" element={<ProductAnalysis />} />
            <Route path="/rankHistoryWatch" element={<RankHistoryWatch />} />
            <Route path="/autoComplete" element={<AutoComplete />} />
            <Route path="/productGenerate" element={<ProductGenerate />} />
            <Route path="/grade-guide" element={<GradeGuide />} />
            <Route path="/payment/history" element={<PaymentHistory />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/fail" element={<PaymentFail />} />
            <Route path="/slot-guide" element={<SlotGuide />} />
            <Route path="/slot-purchase" element={<SlotPurchase />} />
        </Route>
    </Route>
);

export const router = createBrowserRouter(routes);
